// Constants and Basic Types
const CORE_API_TOKEN_ID = 1 // Must be same with CORE_API_TOKEN_ID from env
const STARS_TOKEN_ID = 9

export type CustomKind = "background" | "cards" | "emoji"
export type CurrencyType = "balance" | number
export type Rarity = "common" | "rare" | "epic"

// Core interfaces with strict typing
export interface PriceOption {
  readonly currency: CurrencyType
  readonly price: number
}

export interface CustomItem {
  bought: boolean
  readonly name: string
  readonly previewSrc?: string
  prices: PriceOption[]
  readonly rarity: Rarity
  readonly src: string
}

// Extended item interface that includes custom prices
export interface CustomItemInput {
  name: string
  previewSrc?: string
  prices?: PriceOption[]
  src: string
}

// Price setter interface
export interface PriceSetter {
  setPrice(currency: CurrencyType, newPrice: number): void

  setPrices(newPrices: PriceOption[]): void
}

// Enhanced CustomItem with price setting capabilities
export interface MutableCustomItem extends CustomItem, PriceSetter {}

// Simplified price configurations
const priceConfigs = {
  free: [{ currency: "balance", price: 0 }] as const,
  balanceOnly(price: number) {
    return [{ currency: "balance", price }] as const
  },
  starsOnly(price: number) {
    return [{ currency: STARS_TOKEN_ID, price }] as const
  },
  both(balancePrice: number, starsPrice: number) {
    return [
      { currency: STARS_TOKEN_ID, price: starsPrice },
      { currency: "balance", price: balancePrice },
    ] as const
  },
}

// Asset type and rarity-based default price configurations
const defaultAssetPrices: Record<CustomKind, Record<Rarity, PriceOption[]>> = {
  cards: {
    common: [...priceConfigs.both(400, 40)], // ~10:1 ratio
    rare: [...priceConfigs.both(1000, 100)], // Aligned with 500:100 conversion
    epic: [...priceConfigs.both(2000, 200)], // Aligned with 1200:200 conversion
  },
  background: {
    common: [...priceConfigs.both(200, 20)], // Maintains 10:1 ratio
    rare: [...priceConfigs.both(400, 40)], // Same as common cards
    epic: [...priceConfigs.both(800, 80)], // Below rare cards price point
  },
  emoji: {
    common: [...priceConfigs.free],
    rare: [...priceConfigs.balanceOnly(100)], // Affordable cosmetic
    epic: [...priceConfigs.starsOnly(25)], // Premium cosmetic
  },
}

// Helper function to merge custom prices with default prices
function mergePrices(
  defaultPrices: PriceOption[],
  customPrices?: PriceOption[]
): PriceOption[] {
  if (!customPrices) return [...defaultPrices]

  if (customPrices.some((p) => p.price === 0)) return [...customPrices]

  const mergedPrices = [...defaultPrices]

  customPrices.forEach((customPrice) => {
    const existingIndex = mergedPrices.findIndex(
      (p) => p.currency === customPrice.currency
    )
    if (existingIndex === -1) mergedPrices.push(customPrice)
    else mergedPrices[existingIndex] = customPrice
  })

  return mergedPrices
}

// Enhanced helper function for creating items with price setting capabilities
function createItems(
  items: CustomItemInput[],
  kind: CustomKind,
  rarity: Rarity
): MutableCustomItem[] {
  return items.map((item) => {
    const defaultPrices = defaultAssetPrices[kind][rarity]
    let prices = mergePrices(defaultPrices, item.prices)

    const mutableItem: MutableCustomItem = {
      ...item,
      bought: false,
      rarity,
      prices,

      setPrice(currency: CurrencyType, newPrice: number) {
        const priceIndex = prices.findIndex((p) => p.currency === currency)

        if (priceIndex === -1) prices.push({ currency, price: newPrice })
        else prices[priceIndex] = { currency, price: newPrice }
      },

      setPrices(newPrices: PriceOption[]) {
        prices = [...newPrices]
      },
    }

    return mutableItem
  })
}

// Card definitions with custom prices support
const cards = {
  common: createItems(
    [
      {
        name: "default",
        previewSrc: "default.png",
        src: "default.jpg",
        prices: [...priceConfigs.free],
      },
    ],
    "cards",
    "common"
  ),
  rare: createItems(
    [
      {
        name: "blue",
        previewSrc: "blue.png",
        src: "blue.jpg",
      },
    ],
    "cards",
    "rare"
  ),
  epic: createItems([], "cards", "epic"),
}

// Export cards list
export const cardsList = [
  ...cards.common.map((card) => ({ ...card, bought: card.name === "default" })),
  ...cards.rare,
  ...cards.epic,
]

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
export const defaultCardSrc = cardsList.find(
  (card) => card.name === "default"
)?.src!

// Background definitions
const backgrounds = {
  common: createItems(
    [
      {
        name: "default",
        previewSrc: "default.jpg",
        src: "default.jpg",
        prices: [...priceConfigs.free],
      },
    ],
    "background",
    "common"
  ),
  rare: createItems(
    [
      {
        name: "blur",
        previewSrc: "blur.jpg",
        src: "blur.jpg",
      },
      {
        name: "clever",
        previewSrc: "clever.jpg",
        src: "clever.jpg",
      },
      {
        name: "flowers-1",
        previewSrc: "flowers-1.jpg",
        src: "flowers-1.jpg",
      },
      {
        name: "flowers-2",
        previewSrc: "flowers-2.jpg",
        src: "flowers-2.jpg",
      },
      {
        name: "forest-1",
        previewSrc: "forest-1.jpg",
        src: "forest-1.jpg",
      },
      {
        name: "forest-2",
        previewSrc: "forest-2.jpg",
        src: "forest-2.jpg",
      },
      {
        name: "forest-3",
        previewSrc: "forest-3.jpg",
        src: "forest-3.jpg",
      },
      {
        name: "gradient-1",
        previewSrc: "gradient-1.jpg",
        src: "gradient-1.jpg",
      },
      {
        name: "gradient-2",
        previewSrc: "gradient-2.jpg",
        src: "gradient-2.jpg",
      },
      {
        name: "gradient-3",
        previewSrc: "gradient-3.jpg",
        src: "gradient-3.jpg",
      },
      {
        name: "gradient-4",
        previewSrc: "gradient-4.jpg",
        src: "gradient-4.jpg",
      },
      {
        name: "ice",
        previewSrc: "ice.jpg",
        src: "ice.jpg",
      },
      {
        name: "leaf",
        previewSrc: "leaf.jpg",
        src: "leaf.jpg",
      },
      {
        name: "mountain-1",
        previewSrc: "mountain-1.jpg",
        src: "mountain-1.jpg",
      },
      {
        name: "mountain-2",
        previewSrc: "mountain-2.jpg",
        src: "mountain-2.jpg",
      },
      {
        name: "mountain-3",
        previewSrc: "mountain-3.jpg",
        src: "mountain-3.jpg",
      },
      {
        name: "mountain-4",
        previewSrc: "mountain-4.jpg",
        src: "mountain-4.jpg",
      },
      {
        name: "sheeps",
        previewSrc: "sheeps.jpg",
        src: "sheeps.jpg",
      },
      {
        name: "space-1",
        previewSrc: "space-1.jpg",
        src: "space-1.jpg",
      },
      {
        name: "space-2",
        previewSrc: "space-2.jpg",
        src: "space-2.jpg",
      },
      {
        name: "space-3",
        previewSrc: "space-3.jpg",
        src: "space-3.jpg",
      },
      {
        name: "space-4",
        previewSrc: "space-4.jpg",
        src: "space-4.jpg",
      },
      {
        name: "space-circle",
        previewSrc: "space-circle.jpg",
        src: "space-circle.jpg",
      },
      {
        name: "spider",
        previewSrc: "spider.jpg",
        src: "spider.jpg",
      },
      {
        name: "sunset-gradient",
        previewSrc: "sunset-gradient.jpg",
        src: "sunset-gradient.jpg",
      },
    ],
    "background",
    "rare"
  ),
  epic: createItems([], "background", "epic"),
}

// Export background list
export const backgroundList = [
  ...backgrounds.common.map((bg) => ({ ...bg, bought: bg.name === "default" })),
  ...backgrounds.rare,
  ...backgrounds.epic,
]

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
export const defaultBackgroundSrc = backgroundList.find(
  (background) => background.name === "default"
)?.src!

// Emoji definitions
const emojiNames = [
  "smile",
  "ok",
  "scare",
  "cry",
  "love",
  "angry",
  "sleep",
  "clown",
  "death",
] as const

// Export emoji list
export const emoji = createItems(
  emojiNames.map((name) => ({
    name,
    src: `${name}.svg`,
  })),
  "emoji",
  "common"
).map((item) => ({ ...item, bought: true }))
